'use client'

import { cn } from '@/lib/utils'
import { Icon, InputFake, Popover } from '@/ui/components'
import { memo, useEffect, useRef, useState } from 'react'

interface TimePickerProps {
  label: string
  placeholder?: string
  description?: string
  name?: string
  id?: string
  selected?: string
  required?: boolean
  autoComplete?: string
  disabled?: boolean
  inputClassName?: string
  className?: string
  open?: boolean
  minuteStep?: number
  defaultTime?: string
  onOpenChange?: (open: boolean) => void
  onTimeSelect?: (value: string) => void
  onTimeClear?: () => void
}

const pad = (value: number) => String(value).padStart(2, '0')

export function TimePicker({
  label,
  placeholder = 'hh:mm',
  description,
  id = '',
  name,
  selected,
  required = false,
  autoComplete = 'off',
  disabled = false,
  inputClassName,
  className,
  open,
  minuteStep = 5,
  defaultTime = '12:30',
  onOpenChange,
  onTimeSelect,
  onTimeClear
}: TimePickerProps) {
  const [time, setTime] = useState<string | undefined>(selected)
  const [previousSelected, setPreviousSelected] = useState<string | undefined>(
    selected
  )
  if (selected !== previousSelected) {
    setPreviousSelected(selected)
    setTime(selected)
  }

  const [popoverOpen, setPopoverOpen] = useState(false)
  const popoverIsOpen = disabled ? false : (open ?? popoverOpen)

  const hourRef = useRef<HTMLButtonElement>(null)
  const minuteRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!popoverIsOpen) {
      return
    }
    // Defer to the next frame: the Popover content mounts in a portal with an
    // enter animation, so the refs aren't measurable yet on the same tick.
    const raf = requestAnimationFrame(() => {
      hourRef.current?.scrollIntoView({ block: 'center' })
      minuteRef.current?.scrollIntoView({ block: 'center' })
    })
    return () => cancelAnimationFrame(raf)
  }, [popoverIsOpen])

  const hours = Array.from({ length: 24 }, (_, i) => pad(i))
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) =>
    pad(i * minuteStep)
  )

  const [selectedHour, selectedMinute] = time ? time.split(':') : [undefined]

  // When nothing is selected yet, anchor the scroll position to `defaultTime`
  // (no value is committed — the field stays empty until the user clicks).
  const [defaultHour, defaultMinute] = defaultTime.split(':')
  const anchorHour = selectedHour ?? defaultHour
  const anchorMinute = selectedMinute ?? defaultMinute

  const commit = (hour: string, minute: string) => {
    const value = `${hour}:${minute}`
    setTime(value)
    onTimeSelect?.(value)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled && nextOpen) {
      return
    }
    if (open === undefined) {
      setPopoverOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setTime(undefined)
    handleOpenChange(false)
    onTimeClear?.()
  }

  return (
    <div className={cn('relative flex flex-col gap-3', className)}>
      {/* Visually-hidden real input: `type=hidden`/`readonly` are barred from
          HTML5 constraint validation, so `required` would be ignored. This
          keeps the value in FormData AND participates in native validation. */}
      <input
        id={id}
        name={name}
        type="text"
        tabIndex={-1}
        aria-hidden
        autoComplete={autoComplete}
        required={required}
        value={time ?? ''}
        disabled={disabled}
        onChange={() => {}}
        className="peer pointer-events-none absolute bottom-0 left-0 h-px w-px border-0 p-0 opacity-0"
      />

      <Popover
        triggerClassName={
          'input-focus peer-focus:border-neutral-300 peer-focus:bg-neutral-300'
        }
        className={'z-1000005 w-auto overflow-hidden p-0'}
        open={popoverIsOpen}
        onOpenChange={handleOpenChange}
        button={
          <InputFake
            active={popoverIsOpen}
            label={label}
            value={time ?? ''}
            placeholder={placeholder}
            className={inputClassName}
            disabled={disabled}
          />
        }
      >
        <div className="flex max-h-60 gap-1 p-2">
          <ul
            data-lenis-prevent
            className="flex min-w-14 flex-col gap-1 overflow-y-auto pr-1"
          >
            {hours.map((hour) => {
              const isSelected = hour === selectedHour
              return (
                <li key={hour}>
                  <button
                    type="button"
                    ref={hour === anchorHour ? hourRef : undefined}
                    onClick={() =>
                      commit(hour, selectedMinute ?? defaultMinute)
                    }
                    className={cn(
                      'w-full rounded-md px-3 py-1.5 text-center text-sm',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    )}
                  >
                    {hour}
                  </button>
                </li>
              )
            })}
          </ul>
          <ul
            data-lenis-prevent
            className="flex min-w-14 flex-col gap-1 overflow-y-auto pr-1"
          >
            {minutes.map((minute) => {
              const isSelected = minute === selectedMinute
              return (
                <li key={minute}>
                  <button
                    type="button"
                    ref={minute === anchorMinute ? minuteRef : undefined}
                    onClick={() => commit(selectedHour ?? defaultHour, minute)}
                    className={cn(
                      'w-full rounded-md px-3 py-1.5 text-center text-sm',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    )}
                  >
                    {minute}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </Popover>

      {time && !disabled && (
        <button
          type="button"
          aria-label="Clear time"
          className="global-focus absolute -top-1 -right-1 z-2 flex size-4.5 items-center justify-center rounded-full bg-neutral-100 text-neutral-800"
          onClick={handleClear}
        >
          <Icon className="size-4" icon="material-symbols:close" />
        </button>
      )}

      {description && (
        <div className="text-muted-foreground px-1 text-sm">{description}</div>
      )}
    </div>
  )
}

TimePicker.displayName = 'TimePicker'

export default memo(TimePicker)
